import { Link } from "react-router";
import * as Icons from "lucide-react";
import { ArrowRight } from "lucide-react";
import type { BlockContentMap } from "@veo/types";
import { cn } from "@veo/ui";
import { RevealItem, RevealList } from "../components/motion";
import {
  SectionHeading,
  SectionShell,
  cardHover,
  isDarkBackground,
  type SectionProps,
} from "./SectionRenderer";

/**
 * The responsive ladder for each column count.
 *
 * Five and six only reach their full width at `xl`. A row of five is worth
 * having because a set of values reads as one line of equals, but only where
 * each card still has room for its words — at 1024px five columns leave about
 * 115px of text apiece, which turns a one-word title into two lines and the row
 * into a fence. Below `xl` they fall back to three, which wraps but stays
 * readable.
 */
const COLUMNS: Record<number, string> = {
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2 lg:grid-cols-4",
  5: "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5",
  6: "sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6",
};

/**
 * Where the hairline between two pillars appears — `connected` style only.
 *
 * Only at the breakpoint where the row finally reaches its full column count,
 * and nowhere below it. A divider is a claim about a boundary, and while the
 * row is still wrapping — six pillars showing as 3 + 3 — the boundaries it
 * would draw are accidents of the viewport rather than of the content. The
 * connecting rule carries the row on its own until then.
 *
 * The second class suppresses the divider on whichever items begin a row,
 * since a rule at the container's left edge reads as a table border. It wins
 * over the first without depending on stylesheet order: `:nth-child()` adds a
 * pseudo-class to the selector, so it is simply the more specific of the two.
 *
 * Written out per column count rather than assembled from `columns` — Tailwind
 * only emits utilities it can see as literal text, and a class built at runtime
 * is a class that does not exist in the stylesheet.
 */
const DIVIDERS: Record<number, string> = {
  2: "sm:before:block sm:[&:nth-child(2n+1)]:before:hidden",
  3: "lg:before:block lg:[&:nth-child(3n+1)]:before:hidden",
  4: "lg:before:block lg:[&:nth-child(4n+1)]:before:hidden",
  5: "xl:before:block xl:[&:nth-child(5n+1)]:before:hidden",
  6: "xl:before:block xl:[&:nth-child(6n+1)]:before:hidden",
};

/**
 * The height of the icon well, and so the offset of the connecting rule.
 *
 * The rule is a sibling of the icon rather than an overlay across the row,
 * which is what keeps the two aligned without either one knowing the other's
 * measurements. The divider underneath it is the exception — it is positioned
 * against the item, so it needs the offset spelled out. `h-10` plus the `mt-5`
 * above the rule is 3.75rem, and the two must be changed together.
 */
const ICON_WELL = "h-10";
const RULE_OFFSET = "before:top-[3.75rem]";

/** Lucide by name, falling back to a plain dot for anything unrecognised. */
function iconFor(name: string) {
  return (Icons[(name || "Circle") as keyof typeof Icons] as typeof Icons.Circle) ?? Icons.Circle;
}

/**
 * The `connected` style: pillars on a rule rather than in cards.
 *
 * Each item draws its own segment of the rule, full column width, and the grid
 * is set to no horizontal gutter so the segments meet — which is why the text
 * below carries its own padding instead. An overlay stretched across the row
 * would have been the obvious way to draw one continuous line, and would have
 * needed to know the icon's height to sit at the right level; segments stay
 * aligned with the icons above them because they are siblings of those icons.
 *
 * The first letter is lifted into the accent colour with `::first-letter`
 * rather than by splitting the title into two elements. Same picture, and the
 * title stays a single run of text — a heading cut into "S" and "ecurity" is
 * announced as two fragments by some screen readers, and stops being one word
 * to find-in-page.
 */
function ConnectedPillars({ content, settings }: SectionProps<BlockContentMap["featureCards"]>) {
  const onDark = isDarkBackground(settings);

  /**
   * Honoured here too, on the same terms as the card grid: held back with
   * opacity so the row does not resize under the pointer, only where a fine
   * pointer exists, and never removed from the document. The hover target is
   * the pillar rather than a card, which is the only difference — there is no
   * card in this style to hover.
   */
  const revealOnHover = content.descriptionReveal === "hover";

  return (
    <RevealList className={cn("grid gap-x-0 gap-y-12", COLUMNS[content.columns] ?? COLUMNS[3])}>
      {content.cards.map((card) => {
        const Icon = iconFor(card.icon);

        return (
          <RevealItem
            key={card.title}
            className={cn(
              "group/pillar relative flex flex-col items-center text-center",
              "before:absolute before:bottom-0 before:left-0 before:hidden before:w-px",
              RULE_OFFSET,
              onDark ? "before:bg-navy-600" : "before:bg-ink-200",
              DIVIDERS[content.columns] ?? DIVIDERS[3],
            )}
          >
            <span className={cn("flex items-center justify-center", ICON_WELL)}>
              <Icon
                aria-hidden
                className={cn("size-7", onDark ? "text-gold-300" : "text-gold-600")}
              />
            </span>

            {/*
              The rule, and the node where this pillar meets it. Decoration
              rather than content — the row it draws is already there in the
              markup as a list of items.
            */}
            <span
              aria-hidden
              className={cn(
                "relative mt-5 block h-px w-full",
                onDark ? "bg-gold-300/40" : "bg-gold-500/40",
              )}
            >
              <span
                className={cn(
                  "absolute left-1/2 top-1/2 size-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full",
                  onDark ? "bg-gold-300" : "bg-gold-500",
                )}
              />
            </span>

            <div className="w-full px-4 pt-6">
              <h3
                className={cn(
                  "font-display text-lg",
                  onDark
                    ? "text-cream-50 first-letter:text-gold-300"
                    : "text-navy-900 first-letter:text-gold-600",
                )}
              >
                {card.title}
              </h3>
              {card.description && (
                <p
                  className={cn(
                    "mt-2 text-sm leading-relaxed",
                    onDark ? "text-navy-200" : "text-ink-600",
                    revealOnHover && [
                      "transition-[opacity,transform] duration-300 motion-reduce:transition-none",
                      "pointer-fine:translate-y-1 pointer-fine:opacity-0",
                      "pointer-fine:group-hover/pillar:translate-y-0 pointer-fine:group-hover/pillar:opacity-100",
                      "pointer-fine:group-focus-within/pillar:translate-y-0 pointer-fine:group-focus-within/pillar:opacity-100",
                    ],
                  )}
                >
                  {card.description}
                </p>
              )}

              {card.link?.label && card.link.href && (
                <Link
                  to={card.link.href}
                  {...(card.link.opensInNewTab
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                  className={cn(
                    "mt-4 inline-flex items-center gap-2 text-sm font-semibold transition-colors",
                    onDark
                      ? "text-gold-300 hover:text-gold-200"
                      : "text-navy-700 hover:text-navy-900",
                  )}
                >
                  {card.link.label}
                  <ArrowRight aria-hidden className="size-4" />
                  <span className="sr-only">— {card.title}</span>
                </Link>
              )}
            </div>
          </RevealItem>
        );
      })}
    </RevealList>
  );
}

/** The default treatment: one card per feature. */
function FeatureCardGrid({ content, settings }: SectionProps<BlockContentMap["featureCards"]>) {
  const onDark = isDarkBackground(settings);

  /**
   * Past four columns a card is roughly two-thirds the width it was designed
   * for, so it is given less padding and a smaller title. Without that the
   * generous `p-7` eats most of the card and one-word titles start wrapping —
   * the row would technically be a row and would read as a fence.
   */
  const isTight = content.columns >= 5;

  /**
   * Descriptions that wait for the pointer.
   *
   * Held back with opacity rather than removed from the flow, so the card is
   * the same height whether or not it is showing — a row of five that grew as
   * the pointer crossed it would shove the rest of the page down five times on
   * the way past.
   *
   * `pointer-fine` is what makes this honest on a phone. There is no hover on a
   * touch screen, so a card that only reveals on hover would be five bare words
   * with no way to reach the rest; on a coarse pointer the description is
   * simply always there. And because it is only ever transparent, never
   * `display: none`, a screen reader announces it in both states.
   */
  const revealOnHover = content.descriptionReveal === "hover";

  return (
    <RevealList
      className={cn("grid", isTight ? "gap-4" : "gap-6", COLUMNS[content.columns] ?? COLUMNS[3])}
    >
      {content.cards.map((card) => {
        const Icon = iconFor(card.icon);

        return (
          <RevealItem
            key={card.title}
            className={cn(
              // Every card sits on its own surface, one step off the section
              // background, so it reads as an object rather than an outline.
              "group/card rounded-lg",
              isTight ? "p-5" : "p-7",
              onDark ? "bg-navy-800" : "bg-white shadow-card",
              content.style === "bordered" &&
                (onDark ? "border border-navy-600" : "border border-ink-200"),
              content.style !== "plain" && cardHover(onDark),
            )}
          >
            {card.icon && (
              <span
                className={cn(
                  "inline-flex rounded-md p-2.5",
                  onDark ? "bg-navy-700 text-gold-300" : "bg-navy-900 text-gold-300",
                )}
              >
                <Icon aria-hidden className="size-5" />
              </span>
            )}
            <h3
              className={cn(
                "font-display",
                isTight ? "mt-3 text-lg" : "mt-4 text-xl",
                onDark ? "text-cream-50" : "text-navy-900",
              )}
            >
              {card.title}
            </h3>
            {card.description && (
              <p
                className={cn(
                  "mt-2 leading-relaxed",
                  isTight && "text-sm",
                  onDark ? "text-navy-200" : "text-ink-600",
                  revealOnHover && [
                    "transition-[opacity,transform] duration-300 motion-reduce:transition-none",
                    "pointer-fine:translate-y-1 pointer-fine:opacity-0",
                    "pointer-fine:group-hover/card:translate-y-0 pointer-fine:group-hover/card:opacity-100",
                    // A card carrying a link can be reached by keyboard, and
                    // when it is, its description comes with it.
                    "pointer-fine:group-focus-within/card:translate-y-0 pointer-fine:group-focus-within/card:opacity-100",
                  ],
                )}
              >
                {card.description}
              </p>
            )}

            {/*
              Optional per-card link. The schema has always carried one and
              the section editor now offers it, so a card that has been given
              a destination has to show it — a link an editor sets and the
              page ignores is worse than no link field at all.
            */}
            {card.link?.label && card.link.href && (
              <Link
                to={card.link.href}
                {...(card.link.opensInNewTab
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
                className={cn(
                  "mt-5 inline-flex items-center gap-2 text-sm font-semibold transition-colors",
                  onDark
                    ? "text-gold-300 hover:text-gold-200"
                    : "text-navy-700 hover:text-navy-900",
                )}
              >
                {card.link.label}
                <ArrowRight aria-hidden className="size-4" />
                {/* Names the destination for a screen reader reading links
                    out of context, where "Learn more" alone is useless. */}
                <span className="sr-only">— {card.title}</span>
              </Link>
            )}
          </RevealItem>
        );
      })}
    </RevealList>
  );
}

export default function FeatureCardsBlock(props: SectionProps<BlockContentMap["featureCards"]>) {
  return (
    <SectionShell settings={props.settings}>
      <SectionHeading
        eyebrow={props.content.eyebrow}
        heading={props.content.heading}
        intro={props.content.intro}
        settings={props.settings}
      />

      {/*
        `connected` is not a card treatment with the surface switched off — it
        is a different arrangement of the same content, so it gets its own
        renderer rather than a pile of conditionals through the card markup.
      */}
      {props.content.style === "connected" ? (
        <ConnectedPillars {...props} />
      ) : (
        <FeatureCardGrid {...props} />
      )}
    </SectionShell>
  );
}
